import React, {
  Fragment,
  isValidElement,
  useEffect,
  useMemo,
  useState,
} from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { Check as CheckIcon } from 'react-feather'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Skeleton } from '@material-ui/lab'
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Tooltip,
  Typography,
} from '@material-ui/core'

import theme from 'theme'
import useStyles from './styles'

const InfoCard = ({ step, selectedIndex, index }) => {
  const [showScrollbar, setShowScrollbar] = useState(false)

  const classes = useStyles()

  // Para evitar problemas de renderização do PerfectScrollbar durante transições de visibilidade,
  // é necessário aguardar um pequeno intervalo antes de exibi-lo.
  // Isso garante que o scrollbar seja renderizado corretamente após a transição,
  // permitindo que o usuário role o conteúdo do card mesmo quando ele for maior que a altura disponível.
  useEffect(() => {
    if (step?.selected) {
      const timer = setTimeout(() => {
        setShowScrollbar(true)
      }, 300)

      return () => {
        clearTimeout(timer)
        setShowScrollbar(false)
      }
    }
  }, [step?.selected])

  return (
    <Box
      className={clsx(classes.card, {
        [classes.selectedCard]: step?.selected,
      })}
      bgcolor={
        selectedIndex > 0 && index < selectedIndex
          ? theme.palette.primary.main
          : theme.palette.primary.hover
      }
    >
      <Box display="flex" alignItems="center" gridGap={theme.spacing(2)} pr={2}>
        <Box
          className={classes.numberBox}
          px={step?.concluded ? '5px' : '10px'}
          py={step?.concluded ? '1px' : '6px'}
        >
          {step?.concluded ? (
            <CheckIcon
              size={18}
              strokeWidth={3}
              className={classes.checkIcon}
            />
          ) : (
            <Typography variant="h6" className={classes.number}>
              {index + 1}
            </Typography>
          )}
        </Box>
        <Tooltip title={step?.title}>
          <Typography variant="h6" noWrap>
            {step?.title}
          </Typography>
        </Tooltip>
      </Box>
      {step?.selected && (
        <Fragment>
          <PerfectScrollbar
            key={showScrollbar ? 'scrollbar-visible' : 'scrollbar-hidden'}
            className={classes.scrollContainer}
            options={{
              suppressScrollX: true,
              suppressScrollY: showScrollbar ? false : true,
              wheelPropagation: false,
            }}
          >
            <Box
              maxHeight="100%"
              pr={2}
              display="flex"
              flexDirection="column"
              gridGap={theme.spacing(1)}
            >
              {isValidElement(step?.description) ? (
                step?.description
              ) : Array.isArray(step?.description) ? (
                step?.description?.map((desc, descIndex) => (
                  <Typography key={descIndex}>{desc}</Typography>
                ))
              ) : (
                <Typography>{step?.description}</Typography>
              )}
            </Box>
          </PerfectScrollbar>
          {step?.action && (
            <Box display="flex" justifyContent="flex-end" pr={2}>
              {step?.action}
            </Box>
          )}
        </Fragment>
      )}
    </Box>
  )
}

const TutorialTrails = ({
  steps,
  currentStep,
  iframe,
  isLoading = false,
  onChange,
}) => {
  const classes = useStyles()

  const processedSteps = useMemo(
    () =>
      steps.map((step, index) => ({
        ...step,
        selected: index === currentStep,
      })),
    [steps, currentStep],
  )

  return (
    <Box className={classes.root}>
      <Box className={classes.iframeContainer}>
        {isLoading ? (
          <Skeleton variant="rect" width="100%" height="100%" />
        ) : (
          <iframe
            src={iframe}
            frameBorder="0"
            allow="fullscreen; picture-in-picture; clipboard-write"
            title="Tickets"
            className={classes.iframe}
          />
        )}
      </Box>
      <Grid container spacing={1} alignItems="stretch">
        {processedSteps?.map((step, index) =>
          isLoading ? (
            <Grid
              key={index}
              item
              xs={12}
              md={index === 0 ? 6 : 3}
              className={classes.gridItem}
            >
              <Skeleton variant="rect" width="100%" height={252} />
            </Grid>
          ) : (
            <Grid
              key={index}
              item
              xs={12}
              md={step?.selected ? 6 : 3}
              className={classes.gridItem}
            >
              {step?.selected ? (
                <InfoCard
                  step={step}
                  selectedIndex={currentStep}
                  index={index}
                />
              ) : (
                <Card
                  className={clsx(classes.contentCard, {
                    [classes.originCard]: !step?.disabled,
                    [classes.disabledCard]: step?.disabled,
                  })}
                >
                  <CardActionArea
                    className={classes.contentCard}
                    onClick={() => onChange(index)}
                    disabled={step?.disabled}
                  >
                    <CardContent className={classes.contentCard}>
                      <InfoCard
                        step={step}
                        selectedIndex={currentStep}
                        index={index}
                      />
                    </CardContent>
                  </CardActionArea>
                </Card>
              )}
            </Grid>
          ),
        )}
      </Grid>
    </Box>
  )
}

TutorialTrails.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.element,
      ]).isRequired,
      selected: PropTypes.bool,
      concluded: PropTypes.bool,
      action: PropTypes.element,
    }),
  ),
  currentStep: PropTypes.number.isRequired,
  iframe: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
}

export default TutorialTrails
